import { addDays, daysBetween, formatLongWeekday, parseIsoDate, todayIso } from '../../lib/date';

export interface DayNavigatorProps {
  /** The shown day, `yyyy-MM-dd`. */
  date: string;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  /** Fired with a `yyyy-MM-dd` string when the user picks a date from the native picker. */
  onDateChange: (date: string) => void;
}

function relativeLabel(iso: string): string {
  const diffDays = daysBetween(todayIso(), iso);
  if (diffDays === 0) return 'Today';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays === 1) return 'Tomorrow';
  return parseIsoDate(iso).toLocaleDateString(undefined, { weekday: 'long' });
}

/**
 * Sticky one-day header: `‹  Today / Thu, Jul 30, 2026  ›`.
 *
 * The primary line is a relative label (Today / Yesterday / Tomorrow / weekday) and grows
 * a small `brand-orange` dot on today — a non-text indicator, since `brand-orange` text at
 * this weight/size fails WCAG contrast on white. Tapping anywhere on the centre label opens
 * the platform's native date picker — it's a transparent `<input type="date">` laid over
 * the text, which is the only technique that reliably opens the wheel picker on iOS Safari.
 * A `Today` pill appears in the same row (so the header height never changes) whenever the
 * shown day is not today.
 *
 * The visible label is `aria-hidden`; the chevrons and date button carry the spoken day
 * names instead (via `formatLongWeekday`) so a screen-reader user always hears which day
 * every row below is scoped to.
 *
 * All three controls are ≥44px touch targets.
 */
export function DayNavigator({ date, onPrev, onNext, onToday, onDateChange }: DayNavigatorProps) {
  const isToday = date === todayIso();
  const primary = relativeLabel(date);
  const secondary = parseIsoDate(date).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const prevDate = addDays(date, -1);
  const nextDate = addDays(date, 1);
  const spokenCurrent = formatLongWeekday(date);
  const currentYear = parseIsoDate(date).getFullYear();

  const chevronClass =
    'min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md text-2xl leading-none text-brand-blue active:bg-slate-100';

  return (
    <div className="flex items-center gap-1 px-1 py-1 bg-white border-b border-slate-200">
      <button
        type="button"
        onClick={onPrev}
        aria-label={`Previous day, ${formatLongWeekday(prevDate)}`}
        className={chevronClass}
      >
        <span aria-hidden="true">‹</span>
      </button>

      <div className="relative flex-1 min-w-0 text-center">
        <div className="py-1" aria-hidden="true">
          <div className="flex items-center justify-center gap-1.5 text-base font-semibold leading-tight truncate text-brand-charcoal">
            {/* Today is carried by this dot, not text color — text-brand-orange on white
                is only 3.33:1, which fails for 16px semibold. A non-text indicator only
                needs 3:1. */}
            {isToday && <span aria-hidden="true" className="h-2 w-2 rounded-full bg-brand-orange shrink-0" />}
            {primary}
          </div>
          <div className="text-xs text-slate-500 leading-tight truncate">{secondary}</div>
        </div>
        <input
          type="date"
          value={date}
          onChange={(e) => e.target.value && onDateChange(e.target.value)}
          aria-label={`Change date. Currently ${spokenCurrent}, ${currentYear}`}
          // Transparent overlay: the styled text above is what the user sees, but the tap
          // (and keyboard focus) lands on the real control. text-base keeps iOS from
          // zooming the page when it focuses.
          className="absolute inset-0 w-full h-full min-h-[44px] opacity-0 text-base cursor-pointer"
        />
      </div>

      {!isToday && (
        <button
          type="button"
          onClick={onToday}
          className="min-h-[44px] px-3 rounded-md text-sm font-medium text-brand-blue active:bg-slate-100"
        >
          Today
        </button>
      )}

      <button
        type="button"
        onClick={onNext}
        aria-label={`Next day, ${formatLongWeekday(nextDate)}`}
        className={chevronClass}
      >
        <span aria-hidden="true">›</span>
      </button>
    </div>
  );
}
